import type { Envelope, DeliveryOverviewData, PeriodKey } from '../api/types.js';
import {
  CHART_DEFAULTS,
  CLIENT_DEFAULTS,
  DELIVERY_CHART_DEFAULTS,
  DELIVERY_DEFAULTS,
  TABLE_DEFAULTS,
} from '../state/filters.js';
import { UI_REGIONS } from '../theme/tokens.js';
import type { DataStore, QueryDescriptor } from './dataStore.js';
import { queries } from './queries.js';

/**
 * What "all the data" means.
 *
 * The whole read model is not a fixed set of responses — it is a product of scopes and filter
 * combinations that runs to tens of thousands of requests. So this file draws an explicit line,
 * and the line is: **every page, at its documented default filters, plus one level of
 * drill-down.** That is the set a reader reaches by opening the app and clicking once, which is
 * what "fetch it before they ask" is worth paying for.
 *
 * The three deliberate exclusions, each because the cost is real and the benefit is not:
 *
 * - **The 135 per-recruiter payloads.** They are two clicks deep, one per person, and a reader
 *   opens a handful. They cache on first view like anything else — a prefetch of all of them
 *   would be more requests than the rest of this manifest combined.
 * - **Non-default filter combinations.** Source × targeting × specialty × grain is 36 variants of
 *   each chart. The table's combinations are already precomputed *inside* the response
 *   (`metricsByCombo`), so flipping those controls does not refetch at all; only the chart does,
 *   and only for the combination actually chosen.
 * - **Account search.** Its key is whatever someone is typing.
 *
 * Overview *is* fetched for all seven periods, because the period control is the one table filter
 * that does re-request, and seven small responses buy an instant Timeline.
 *
 * ## Stages, not one list
 *
 * Leaders and managers are not known until the Delivery overview has been read — there is no
 * static list of them anywhere in the client. So the plan is built in stages, each stage reading
 * what the previous one stored. Stage order is also priority order: the landing page first, the
 * furthest drill-down last, so an interrupted prefetch has still done the most useful part.
 */

const OVERVIEW_PERIODS: readonly PeriodKey[] = ['MTD', '30d', '90d', '6M', '12M', '24M', 'YTD'];

/** `ALL` is the Overview chart's default scope — every region summed, not a region. */
const ALL_REGIONS = 'ALL';

export interface PrefetchStage {
  name: string;
  /** Built at run time, from whatever earlier stages have already stored. */
  build: (store: DataStore) => Array<QueryDescriptor<unknown>>;
}

export const PREFETCH_STAGES: readonly PrefetchStage[] = [
  {
    name: 'Snapshot',
    build: () => [
      queries.sync(),
      // The landing page, at the period it opens on. First because it is the page a reader is
      // most likely to be looking at while the rest of this runs.
      queries.overview(TABLE_DEFAULTS.period),
      queries.regionTimeseries(ALL_REGIONS, CHART_DEFAULTS),
      // The Audit list (spec 007, FR-AU33): small, and it carries the nav badge. Prefetching it
      // is also what hides the audit service's ~8 s cold start from the first page open. When
      // the page is switched off the request answers 503, which the store simply does not keep.
      queries.auditList(),
      // Alerts (spec 008, FR-AL04): the sidebar badge and the header bell show its count on every
      // page, and the bell may never fetch for itself — so it has to be here at sign-in.
      queries.auditAlerts(),
    ],
  },

  {
    name: 'Regions overview',
    build: () => [
      ...OVERVIEW_PERIODS.filter((period) => period !== TABLE_DEFAULTS.period).map((period) =>
        queries.overview(period),
      ),
      // One chart per region, at the pills' single-selection scope.
      ...UI_REGIONS.map((code) => queries.regionTimeseries(code, CHART_DEFAULTS)),
    ],
  },

  {
    name: 'Region detail',
    build: () => [
      ...UI_REGIONS.flatMap((code) => [
        queries.regionDetail(code),
        queries.regionClients(code, CLIENT_DEFAULTS),
      ]),
    ],
  },

  {
    name: 'Delivery',
    build: () => [
      queries.delivery(DELIVERY_DEFAULTS),
      queries.deliveryTimeseries({
        scopeType: 'all',
        scopeKey: null,
        ...DELIVERY_CHART_DEFAULTS,
      }),
    ],
  },

  {
    name: 'Delivery teams',
    build: (store) => {
      // Derived, so it is empty if the stage above failed — which is correct: a leader list
      // guessed from nothing would be a list of 404s.
      const overview = store.get<Envelope<DeliveryOverviewData>>(
        queries.delivery(DELIVERY_DEFAULTS).key,
      );
      const data = overview?.data;
      if (!data) return [];

      return [
        ...data.leaders.flatMap((leader) => [
          queries.deliveryLeader(leader.leaderKey, DELIVERY_DEFAULTS),
          queries.deliveryTimeseries({
            scopeType: 'leader',
            scopeKey: leader.leaderKey,
            ...DELIVERY_CHART_DEFAULTS,
          }),
        ]),
        // Managers, but not their recruiters — see the exclusions above.
        ...data.managers.map((manager) =>
          queries.deliveryManager(manager.managerId, DELIVERY_DEFAULTS),
        ),
      ];
    },
  },

  {
    name: 'Specialties',
    build: () => [
      queries.specialties(null),
      // The region chips re-request, because the region scope is a request parameter that
      // re-scopes the offering cards too.
      ...UI_REGIONS.map((code) => queries.specialties(code)),
    ],
  },
];

/** Total requests the plan will consider, for the Settings readout. Stage 5 is unknown up front. */
export function plannedCount(store: DataStore): number {
  return PREFETCH_STAGES.reduce((total, stage) => total + stage.build(store).length, 0);
}
